import { zodResolver } from '@hookform/resolvers/zod';
import { Grid2 } from '@mui/material';
import ButtonComponent from 'components/ButtonComponent';
import CheckboxComponent from 'components/CheckboxComponent';
import DropdownComponent from 'components/DropdownComponent';
import MailSelectComponent from 'components/MailSelectComponent';
import TelegramSelectComponent from 'components/TelegramSelectComponent';
import WhatsAppSelectComponent from 'components/WhatsAppSelectComponent';
import { AppContext } from 'context/AppContextProvider';
import { useSmtpIntegration } from '../../../../hooks/useSmtpIntegration';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useGetAllDevicesByLicenseAsync } from 'services/Devices/hooks/useMutation';
import { ILprDeviceProps } from 'services/Devices/LprService/types';
import { RecognitionCameraConfigEditionFormSchema } from 'services/Devices/Recognition/Cameras/Intelbras/RecognitionCameraService/schemas';
import { useUpdateConfigControlIdAsync } from 'services/Devices/Recognition/Controllers/ControlId/hooks/useMutation';
import { useUpdateConfigFacialControllerAsync } from 'services/Devices/Recognition/Controllers/hooks/useMutation';
import { IDeviceSimpleProps } from 'services/Devices/types';
import zod from 'zod';

interface FacialControllerDeviceConfigProps {
  isOpen: boolean;
  selectedDevice?: any;
  setSelectedDevice?: Dispatch<SetStateAction<any>>;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onSaved?: () => void;
}

type FacialControllerDeviceConfigEditionFormInput = zod.infer<
  typeof RecognitionCameraConfigEditionFormSchema
>;

const FacialControllerDeviceConfig = ({
  setIsOpen,
  setSelectedDevice,
  selectedDevice,
  onSaved,
}: FacialControllerDeviceConfigProps) => {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FacialControllerDeviceConfigEditionFormInput>({
    resolver: zodResolver(RecognitionCameraConfigEditionFormSchema),
    defaultValues: {
      telegramIds: [],
      whatsAppNumbers: [],
      emailAddresses: [],
      isNotifyLogEvent: false,
      functionType: 'COMMON',
      smtpIntegrationId: null,
      hasDoorConfirmation: false,
    },
  });
  
  const { showSnackbar } = useContext(AppContext);
  const [selectedDeviceToConfig, setSelectedDeviceToConfig] =
    useState<ILprDeviceProps>();
  const { smtpValue, onSmtpChange } = useSmtpIntegration(control, setValue);
  const [devicesToContext, setDevicesToContext] = useState<
    IDeviceSimpleProps[]
  >([]);
  const {
    mutateAsync: mutateGetAllByLicenseAsync,
    isLoading: isLoadingGetAllByLicenseAsync,
  } = useGetAllDevicesByLicenseAsync();

  const {
    mutate: mutateUpdateConfigFacial,
    isLoading: isLoadingUpdateConfigFacial,
  } = useUpdateConfigFacialControllerAsync();

  const { mutate: mutateControlId } = useUpdateConfigControlIdAsync();
  const handleClose = () => {
    setIsOpen(false);
    reset();
    if (setSelectedDevice) setSelectedDevice({});
  };

  const functionOptions = [
    { label: 'Controlador de saída de perímetro', value: 'EXIT' },
    { label: 'Controlador de entrada de perímetro', value: 'ENTRY' },
    { label: 'Controlador padrão', value: 'COMMON' },
    { label: 'Controlador TAG Veicular', value: 'TAG' },

  ];

  const onSubmit = (data: FacialControllerDeviceConfigEditionFormInput) => {
    if (selectedDevice) {
      if (selectedDevice.brand === 'CONTROLID') {
        mutateControlId(
          { data, id: selectedDevice.id },
          {
            onSuccess: () => {
              showSnackbar('Configuração atualizada com sucesso.', 'success');
              if (setSelectedDevice) setSelectedDevice({});
              reset();
              setIsOpen(false);
              if (onSaved) onSaved();
            },
          },
        );
        return;
      } else {
        mutateUpdateConfigFacial(
          { data, id: selectedDevice.id },
          {
            onSuccess: () => {
              showSnackbar('Configuração atualizada com sucesso.', 'success');
              if (setSelectedDevice) setSelectedDevice({});
              reset();
              setIsOpen(false);
              if (onSaved) onSaved();
            },
          },
        );
      }
    }
  };

  useEffect(() => {
    mutateGetAllByLicenseAsync({}).then(setDevicesToContext);
  }, []);

  useEffect(() => {
    if (selectedDevice) {
      reset({
        ...selectedDevice,
        smtpIntegrationId: selectedDevice?.smtpIntegrationId || null,
      });
      setSelectedDeviceToConfig(selectedDevice);
    } else reset();
  }, [selectedDevice, reset]);
  return (
    <Grid2 container columnSpacing={3}>
      <Controller
        render={({ field: { value, onChange } }) => (
          <WhatsAppSelectComponent
            onChange={onChange}
            value={value}
            gridSize={12}
          />
        )}
        control={control}
        name={'whatsAppNumbers'}
      />
      <Controller
        render={({ field: { value, onChange } }) => (
          <TelegramSelectComponent
            onChange={onChange}
            value={value}
            gridSize={12}
          />
        )}
        control={control}
        name={'telegramIds'}
      />
      <Controller
        render={({ field: { value, onChange } }) => (
          <MailSelectComponent
            onChange={onChange}
            value={value}
            smtpValue={smtpValue}
            onSmtpChange={onSmtpChange}
          />
        )}
        control={control}
        name={'emailAddresses'}
      />
      <Grid2 size={12}>
        <Controller
          control={control}
          name="contextCameraId"
          render={({ field: { value, onChange } }) => {
            return (
              <DropdownComponent
                onChange={onChange}
                optionLabel="name"
                optionValue="id"
                options={devicesToContext}
                value={value}
                label="Dispositivo de contexto"
              />
            );
          }}
        />
      </Grid2>


      <Grid2 size={{ md: 6, xs: 12 }}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <DropdownComponent
              onChange={onChange}
              value={value}
              label={'Função'}
              optionLabel={'label'}
              options={functionOptions}
              optionValue={'value'}
            />
          )}
          name={'functionType'}
          control={control}
        />
      </Grid2>
      <Grid2 size={6}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <CheckboxComponent
              label="Eventos de Log"
              onChange={onChange}
              value={value}
            />
          )}
          name={'isNotifyLogEvent'}
          control={control}
        />
      </Grid2>
      <Grid2 size={6}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <CheckboxComponent
              label="Confirmação de Porta (Acesso)"
              onChange={onChange}
              value={value}
            />
          )}
          name={'hasDoorConfirmation'}
          control={control}
        />
      </Grid2>
      <Grid2 size={12} sx={{ mt: 1 }}>
        <ButtonComponent
          fullWidth
          onClick={handleSubmit(onSubmit)}
          loading={isLoadingGetAllByLicenseAsync || isLoadingUpdateConfigFacial}
        >
          Salvar
        </ButtonComponent>
      </Grid2>
    </Grid2>
  );
};

export default FacialControllerDeviceConfig;
